import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pause, Play } from "lucide-react";
import { clock } from "@/lib/sunoVoicesApi";

/** Big play button + progress for one comparison clip; plays only `start`–`end` and pauses the others in `group`. */
export default function ComparePlayer({ src, start = 0, end = null, duration = null, testId, group, accent }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(end != null ? end - start : duration || 0);
  useEffect(() => {
    const a = ref.current;
    const set = group.current;
    set.add(a);
    return () => set.delete(a);
  }, [group]);
  useEffect(() => { setT(0); setPlaying(false); }, [src]);
  const toggle = () => {
    const a = ref.current;
    if (!a.paused) { a.pause(); return; }
    if (a.currentTime < start || (end != null && a.currentTime >= end - 0.1) || a.ended) a.currentTime = start;
    a.play().catch(() => toast.error("Couldn't play this clip"));
  };
  const onPlay = () => { group.current.forEach((o) => { if (o !== ref.current) o.pause(); }); setPlaying(true); };
  const onTime = (e) => {
    const ct = e.currentTarget.currentTime;
    if (end != null && ct >= end) e.currentTarget.pause();
    setT(Math.max(0, Math.min(ct, end ?? ct) - start));
  };
  const onMeta = (e) => { if (end == null && Number.isFinite(e.currentTarget.duration)) setDur(e.currentTarget.duration); };
  const pct = dur ? Math.min(100, (t / dur) * 100) : 0;
  return (
    <div className="flex items-center gap-4">
      <audio ref={ref} src={src} preload="metadata" data-testid={`${testId}-audio`} onPlay={onPlay} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} onTimeUpdate={onTime} onLoadedMetadata={onMeta} />
      <button type="button" data-testid={`${testId}-play`} data-playing={playing} onClick={toggle}
        className={`grid h-14 w-14 shrink-0 place-items-center rounded-full text-black transition-transform duration-200 hover:scale-105 active:scale-95 ${accent}`}>
        {playing ? <Pause className="h-5 w-5 fill-black" /> : <Play className="ml-0.5 h-5 w-5 fill-black" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10"><div className={`h-full ${accent} transition-[width] duration-200`} style={{ width: `${pct}%` }} /></div>
        <p data-testid={`${testId}-time`} className="mt-2 font-mono text-[11px] text-white/55">{clock(t)} / {clock(dur)}</p>
      </div>
    </div>
  );
}
